import { ThemeProvider, createTheme, Theme } from '@mui/material';

interface BrandThemeProviderProps {
  theme?: Theme;
  children: React.ReactElement | React.ReactElement[];
}

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    action?: string;
  }
  interface TypeText {
    contrastText?: string;
  }
  interface Palette {
    barChart: PaletteColor;
    lineChart: PaletteColor;
    pieChart?: PaletteColor;
    maps: PaletteColor;
    progress?: PaletteColor;
    premium: PaletteColor;
    listingPins?: PaletteColor;
    listingPinsVisited?: PaletteColor;
    parcelBoundaries?: PaletteColor;
    neighborhoodWatch?: PaletteColor;
    marketInsights?: PaletteColor;
    notistackToast?: PaletteColor;
    relatedProperties?: PaletteColor;
    sideNavigation?: PaletteColor;
    labelsAndLinks?: PaletteColor;
    accountInfoModals?: PaletteColor;
    accordions?: PaletteColor;
    dataCard?: PaletteColor;
    aiChip?: PaletteColor;
    customCard?: PaletteColor;
    propertySmartinsights?: PaletteColor;
    upgradeCheck?: PaletteColor;
    threatDetection?: PaletteColor;
    reportSectionSupertitleBackgroundColor?: PaletteColor;
    unclaimedMoneyGrid?: PaletteColor;
    unclaimedMoneyMonitor?: PaletteColor;
  }

  interface PaletteOptions {
    barChart?: SimplePaletteColorOptions;
    lineChart: SimplePaletteColorOptions;
    pieChart?: SimplePaletteColorOptions;
    maps?: SimplePaletteColorOptions;
    progress?: SimplePaletteColorOptions;
    premium?: SimplePaletteColorOptions;
    listingPins?: SimplePaletteColorOptions;
    listingPinsVisited?: SimplePaletteColorOptions;
    parcelBoundaries?: SimplePaletteColorOptions;
    neighborhoodWatch?: SimplePaletteColorOptions;
    marketInsights?: SimplePaletteColorOptions;
    notistackToast?: SimplePaletteColorOptions;
    relatedProperties?: SimplePaletteColorOptions;
    sideNavigation?: SimplePaletteColorOptions;
    labelsAndLinks?: SimplePaletteColorOptions;
    accountInfoModals?: SimplePaletteColorOptions;
    accordions?: SimplePaletteColorOptions;
    dataCard?: SimplePaletteColorOptions;
    aiChip?: SimplePaletteColorOptions;
    customCard?: SimplePaletteColorOptions;
    propertySmartinsights?: SimplePaletteColorOptions;
    upgradeCheck?: SimplePaletteColorOptions;
    threatDetection?: SimplePaletteColorOptions;
    reportSectionSupertitleBackgroundColor?: SimplePaletteColorOptions;
    unclaimedMoneyGrid?: SimplePaletteColorOptions;
    unclaimedMoneyMonitor?: SimplePaletteColorOptions;
  }
}

export const defaultTheme = createTheme({
  typography: {
    button: { textTransform: 'none', fontSize: '1rem', fontWeight: 'bold' },
    fontSize: 16,
    htmlFontSize: 16,
    h1: {
      fontSize: '1.875rem',
      fontWeight: 'bold',
      margin: '0 0 12px 0',
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      margin: '0 0 12px 0',
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 'bold',
      margin: '0 0 12px 0',
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: 'bold',
      margin: '0 0 12px 0',
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 'bold',
      margin: '0 0 12px 0',
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: 'bold',
      margin: '0 0 12px 0',
    },
    caption: {
      letterSpacing: 0,
    },
  },
  components: {
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
  },
  palette: {
    divider: '#E6E7E9',
    barChart: {
      main: '#24537B',
      light: '#C2E0E9',
      dark: '#D3D3D7',
      contrastText: '#000',
    },
    lineChart: {
      main: '#24537B',
      light: '#757575',
      dark: '#DC0015',
      contrastText: '#000',
    },
    pieChart: {
      main: '#24537B',
      light: '#C2E0E9',
      dark: '#D3D3D7',
      contrastText: '#000',
    },
    maps: {
      main: '#DC0015',
      light: '#3388FF',
    },
    notistackToast: {
      main: '#008000',
      light: '#defce6',
      dark: '#005000',
      contrastText: '#ffffff',
    },
    neighborhoodWatch: {
      main: '#4A3B8F',
      light: '#757575',
      dark: '#DC0015',
      contrastText: '#fff',
    },
    marketInsights: {
      main: '#4A3B8F',
      light: '#edebff',
      dark: '#4A3B8F',
      contrastText: '#fff',
    },
    progress: {
      main: '#2D2D39',
      light: '#208732',
      dark: '#FDDB66',
      contrastText: '#E7EBF0',
    },
    premium: {
      main: '#FCC41C',
    },
    listingPinsVisited: {
      main: '#f8a09e',
      light: '#9FCBA2',
      dark: '#9F97C1',
    },
    parcelBoundaries: {
      main: '#FFFFFF',
      dark: '#000000',
    },
    threatDetection: {
      main: '#961616',
      light: '#E1FAEA',
      dark: '#016626',
      contrastText: '#FFF1F1',
    },
    relatedProperties: {
      main: '#f8f8f8',
      light: '#FFF',
      dark: '#2D2D39',
    },
    aiChip: {
      main: '#4A3B8F',
      light: '#9F44EB',
      contrastText: '#fff',
    },
    propertySmartinsights: {
      main: 'linear-gradient(5deg, #000 60%, rgba(42,3,69,1) 80%)',
    },
    upgradeCheck: {
      main: '#74ad47',
    },
    reportSectionSupertitleBackgroundColor: {
      main: 'rgba(95, 45, 176, 1)',
    },
    unclaimedMoneyGrid: {
      main: '#4A3B8F',
      dark: 'rgba(0, 0, 0, 0.87)',
      light: '#f8f8f8',
      contrastText: '#fff',
    },
    unclaimedMoneyMonitor: {
      main: '#FFFFFF',
      light: '#f8f8f8',
      contrastText: '#FFFFFF',
    },
  },
});

export const BrandThemeProvider = ({
  theme,
  children,
}: BrandThemeProviderProps) => {
  return (
    <ThemeProvider theme={{ ...defaultTheme, ...theme }}>
      {children}
    </ThemeProvider>
  );
};
